import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { useAuth } from '../../shared/hooks/useAuth';
import { useSelectedCountry } from '../../shared/hooks/useSelectedCountry';
import { CategoryIcon } from '../../shared/components/ui/CategoryIcon';
import {
  getFeaturedCategories,
  pickCategoryImage,
  type CategorySlug,
} from '../../shared/assets/categoryImages';
import { ThemeToggle } from '../../shared/components/ui/ThemeToggle';

export function ProLandingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, hasRole } = useAuth();
  const { countryCode } = useSelectedCountry();

  const handlePick = (slug: CategorySlug) => {
    const becomeUrl = `/become-provider?category=${slug}`;
    if (!isAuthenticated) {
      navigate(`/pro/register?next=${encodeURIComponent(becomeUrl)}`);
    } else if (hasRole('PROVIDER')) {
      navigate('/dashboard');
    } else {
      navigate(becomeUrl);
    }
  };

  // 4 collage images for the dark left side.
  const collage = [
    pickCategoryImage('hair', 0),
    pickCategoryImage('makeup', 1),
    pickCategoryImage('nail', 0),
    pickCategoryImage('cake', 0),
  ].filter(Boolean);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* TOP BAR */}
      <Box
        sx={{
          py: 1.5,
          px: { xs: 2, md: 4 },
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'background.paper',
          zIndex: 1,
        }}
      >
        <Box
          component={RouterLink}
          to="/"
          sx={{
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            color: 'text.primary',
          }}
          aria-label={`${t('app.name')} PRO`}
        >
          <Box
            component="img"
            src="/logo-noir.png"
            alt={t('app.name')}
            loading="eager"
            sx={{
              display: 'block',
              height: 44,
              width: 'auto',
              objectFit: 'contain',
            }}
          />
          <Typography
            component="span"
            sx={{
              fontWeight: 700,
              fontSize: '0.7rem',
              letterSpacing: '0.18em',
              color: 'primary.main',
            }}
          >
            PRO
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <ThemeToggle size="small" />
        <Button
          color="inherit"
          component={RouterLink}
          to="/pro/login"
          sx={{ fontWeight: 600 }}
        >
          {t('auth.login')}
        </Button>
      </Box>

      {/* SPLIT BODY */}
      <Box
        sx={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        }}
      >
        {/* LEFT — Marketing.  Switches palette with the theme: warm
            paper-tinted surface in light mode, deep near-black in dark
            mode.  Text + accent colours flow from theme tokens so they
            stay legible in both. */}
        <Box
          sx={{
            bgcolor: (theme) =>
              theme.palette.mode === 'dark'
                ? '#0E0B08'
                : theme.palette.background.paper,
            color: 'text.primary',
            p: { xs: 4, md: 6 },
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflow: 'hidden',
            borderRight: (theme) =>
              theme.palette.mode === 'dark'
                ? 'none'
                : `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2.25rem', md: '3rem' },
                fontWeight: 800,
                lineHeight: 1.1,
                mb: 2,
                maxWidth: 520,
                color: 'text.primary',
              }}
            >
              {t('pro.heroTitle')}
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                color: 'text.secondary',
                maxWidth: 480,
                mb: 4,
                fontSize: { xs: '1rem', md: '1.0625rem' },
              }}
            >
              {t('pro.heroSubtitle')}
            </Typography>

            <Stack spacing={1.5} sx={{ mb: 5, maxWidth: 480 }}>
              {[
                t('pro.benefit1'),
                t('pro.benefit2'),
                t('pro.benefit3'),
              ].map((b) => (
                <Stack key={b} direction="row" spacing={1.25} alignItems="center">
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      bgcolor: (theme) =>
                        theme.palette.mode === 'dark'
                          ? 'rgba(255,255,255,0.12)'
                          : `${theme.palette.primary.main}1F`,
                      color: (theme) =>
                        theme.palette.mode === 'dark'
                          ? theme.palette.common.white
                          : theme.palette.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <CheckRoundedIcon sx={{ fontSize: '0.95rem' }} />
                  </Box>
                  <Typography variant="body1" sx={{ color: 'text.primary' }}>
                    {b}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>

          {/* Image collage */}
          {collage.length === 4 && (
            <Box
              sx={{
                display: { xs: 'none', md: 'grid' },
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 1.5,
                maxWidth: 360,
              }}
            >
              {collage.map((url, i) => (
                <Box
                  key={i}
                  component="img"
                  src={url}
                  alt=""
                  sx={{
                    width: '100%',
                    aspectRatio: '1 / 1',
                    objectFit: 'cover',
                    borderRadius: i === 0 || i === 3 ? '50% 50% 8px 8px' : '8px 8px 50% 50%',
                  }}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* RIGHT — Funnel */}
        <Box
          sx={{
            bgcolor: 'background.default',
            p: { xs: 3, md: 6 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Container maxWidth="sm" disableGutters>
            {/* Step indicator */}
            <Stack direction="row" spacing={0.75} sx={{ mb: 4 }}>
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  sx={{
                    flex: 1,
                    height: 4,
                    borderRadius: 999,
                    bgcolor: i === 0 ? 'text.primary' : 'divider',
                  }}
                />
              ))}
            </Stack>

            <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
              {t('pro.funnelTitle')}
            </Typography>

            <Stack spacing={1.5}>
              {getFeaturedCategories(countryCode).map((cat) => (
                <Button
                  key={cat.slug}
                  onClick={() => handlePick(cat.slug)}
                  variant="outlined"
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={{
                    justifyContent: 'space-between',
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    py: 1.5,
                    px: 2,
                    borderRadius: 2,
                    borderColor: 'divider',
                    color: 'text.primary',
                    bgcolor: 'background.paper',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'background.paper',
                    },
                  }}
                  startIcon={
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        bgcolor: 'action.hover',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'primary.main',
                        mr: 0.5,
                      }}
                    >
                      <CategoryIcon code={cat.slug} sx={{ fontSize: '1.25rem' }} />
                    </Box>
                  }
                >
                  <Box sx={{ flex: 1, textAlign: 'left' }}>
                    {t(cat.i18nKey)}
                  </Box>
                </Button>
              ))}
            </Stack>

            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography
                component={RouterLink}
                to="/"
                sx={{
                  color: 'text.secondary',
                  textDecoration: 'underline',
                  textUnderlineOffset: 3,
                  fontSize: '0.9rem',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                {t('pro.notAPro')}
              </Typography>
            </Box>
          </Container>
        </Box>
      </Box>
    </Box>
  );
}
